import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CompetitionScorecard,
  type ScorecardClimber,
  type ScorecardData,
} from '@structural-climbing/ui'
import { AvatarPlaceholder } from '../standings/standingsUi'

interface Props {
  competitionId: string
  /** The competitor's own category: every category splits its own points. */
  categoryId: string | null
  myUserId: string | undefined
  apiUrl: string
  getAccessTokenSilently: () => Promise<string>
}

/**
 * Every route against every climber in the competitor's category, so they can
 * add their own total up and see exactly where each point came from.
 */
export function CompetitionScorecardTab({
  competitionId,
  categoryId,
  myUserId,
  apiUrl,
  getAccessTokenSilently,
}: Props) {
  const [data, setData] = useState<ScorecardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  const load = useCallback(async () => {
    if (!categoryId) return
    setLoading(true)
    setFailed(false)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(
        `${apiUrl}/my-competitions/${competitionId}/scorecard?category=${encodeURIComponent(categoryId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (res.ok) setData((await res.json()) as ScorecardData)
      else setFailed(true)
    } catch {
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }, [competitionId, categoryId, apiUrl, getAccessTokenSilently])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <div className="pt-1">
      <CompetitionScorecard
        data={data}
        loading={loading}
        failed={failed}
        myUserId={myUserId}
        renderClimber={(climber) => (
          <ClimberName climber={climber} apiUrl={apiUrl} isMe={climber.userId === myUserId} />
        )}
      />
    </div>
  )
}

function ClimberName({
  climber,
  apiUrl,
  isMe,
}: {
  climber: ScorecardClimber
  apiUrl: string
  isMe: boolean
}) {
  const { t } = useTranslation()
  // Same rule as the standings table: a photo only when one has been uploaded.
  const avatarUrl =
    climber.avatarExt && climber.avatarUpdatedAt
      ? `${apiUrl}/member-avatar/${encodeURIComponent(climber.userId)}`
      : null
  return (
    <span className="flex min-w-0 items-center gap-2">
      {avatarUrl ? (
        <img src={avatarUrl} alt="" className="h-7 w-7 shrink-0 rounded-full object-cover" />
      ) : (
        <AvatarPlaceholder name={climber.displayName} />
      )}
      <span className="min-w-0 truncate text-sm font-semibold" title={climber.displayName}>
        {climber.displayName}
      </span>
      {isMe && <span className="shrink-0 text-xs text-foreground/40">({t('standings.you')})</span>}
    </span>
  )
}
